// TransferHistoryLog.tsx
import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
  ScrollView,
  View,
  Text,
  TouchableOpacity,
  Pressable,
} from 'react-native';
import { FontAwesome5 } from '@expo/vector-icons';

import { getInventoryTransferData, getInventoryTransferDetailsData } from '../../../../api/Settings';
import { msgStr } from '../../../../common/constants/Message';
import { TextMediumSize } from '../../../../common/constants/Fonts';
import { useAlertModal } from '../../../../common/hooks';
import BasicLayout from '../../../../common/components/CustomLayout/BasicLayout';

import { productsStyle } from './styles/ProductsStyle';

// Modified to accept props for integration with LocationHistory
const TransferHistoryLog = ({ 
  navigation, 
  openInventory, 
  data,
  isChildComponent = false, // Flag to determine if used as child component
  containerStyle = {} // Optional style for container
}) => {
  const initialMount = useRef(true);
  const { showAlert } = useAlertModal();

  // State variables
  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortColumn, setSortColumn] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedBarcode, setSelectedBarcode] = useState(null);
  const [barcodeDetails, setBarcodeDetails] = useState(null);
  const [isDetailsLoading, setIsDetailsLoading] = useState(false);

  // Extract barcodes from parent component data
  const getBarcodes = () => {
    if (data && Array.isArray(data)) {
      return data.map(item => item.barcode).filter(Boolean);
    } else if (data && data.tableData && Array.isArray(data.tableData)) {
      return data.tableData.map(item => item.barcode).filter(Boolean);
    }
    return [];
  };

  // Function to fetch transfer history data
  const getTransferHistoryData = () => {
    setIsLoading(true);
    
    const barcodes = getBarcodes();
    const payload = {
      barcode: undefined
    };
    
    getInventoryTransferData(payload, (jsonRes, status, error) => {
      switch (status) {
        case 200:
          setTableData(jsonRes);
          break;
        case 500:
          showAlert('error', msgStr('serverError'));
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          break;
      }
      setIsLoading(false);
    });
  };

  const fetchBarcodeDetails = (barcode) => {
    if (!barcode) return;
  
    setIsDetailsLoading(true);
    setBarcodeDetails(null);
  
    getInventoryTransferDetailsData({ barcode: [barcode], uniqueOnly: false }, (jsonRes, status, error) => {
      switch (status) {
        case 200:
          setBarcodeDetails(jsonRes || []); // whole array
          break;
        case 500:
          showAlert('error', msgStr('serverError'));
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          break;
      }
      setIsDetailsLoading(false);
    });
  };
  

  useEffect(() => {
    getTransferHistoryData();
    initialMount.current = false;
  }, [data]);

  // Sorting function
  const getSortedData = (data) => {
    if (sortColumn && sortDirection) {
      data.sort((a, b) => {
        let A, B;
        
        // Handle different column types
        switch (sortColumn) {
          case 'oldLocation.locations.location':
            A = a.oldLocation?.locations?.location || '';
            B = b.oldLocation?.locations?.location || '';
            break;
          case 'newLocation.locations.location':
            A = a.newLocation?.locations?.location || '';
            B = b.to_location?.location || '';
            break;
          case 'location_type':
            A = a.location_type || '';
            B = b.location_type || '';
            break;
          case 'product':
            A = a.product?.barcode || '';
            B = b.product?.barcode || '';
            break;
          case 'transfer_date':
            A = new Date(a.transfer_date).getTime();
            B = new Date(b.transfer_date).getTime();
            break;
          default:
            A = a[sortColumn];
            B = b[sortColumn];
        }

        if (typeof A === 'number' && typeof B === 'number') {
          return sortDirection === 'asc' ? A - B : B - A;
        } else {
          const textA = String(A).toUpperCase();
          const textB = String(B).toUpperCase();
          return sortDirection === 'asc' ? textA.localeCompare(textB) : textB.localeCompare(textA);
        }
      });
    }
    return data;
  };

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    return getSortedData([...tableData]);
  }, [tableData, sortColumn, sortDirection]);

  // Handle sorting when column header is clicked
  const sortData = (column) => {
    const direction = column === sortColumn && sortDirection === 'desc' ? 'asc' : 'desc';
    setSortColumn(column);
    setSortDirection(direction);
  };

  // Render table rows
  const renderTableData = () => {
    const rows = [];
    if (filteredAndSortedData.length > 0) {
      filteredAndSortedData.forEach((item, index) => {
        rows.push(
          <View key={index} style={styles.tableRow}>
            <View style={styles.cell}>
            <Pressable onPress={() => {
              setSelectedBarcode(item); // keep local selection
              fetchBarcodeDetails(item.barcode); // fetch latest details
            }}>
              <Text style={{ fontWeight: 'bold' }}>
                {item.barcode || ''}
              </Text>
            </Pressable>
            </View>
            <View style={styles.cell}>
              <Text>{item.display_name || ''}</Text>
            </View>
            <View style={[styles.cell, { flex: 1, flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'flex-start' }]}>
              <Text style={{ color: 'green', fontWeight: 'bold', fontSize: 13 }}>
                {item.new_home_location || 'empty'}
              </Text>
              <View style={{ marginHorizontal: 50 }}>
                <Text style={{ color: 'green', fontSize: 12, lineHeight: 12 }}>⏶</Text>
                <Text style={{ color: 'red', fontSize: 12, lineHeight: 12 }}>⏷</Text>
              </View>
              <Text style={{ color: 'red', fontWeight: 'bold', fontSize: 13 }}>
                {item.old_home_location || 'empty'}
              </Text>
            </View>
            
            <View style={[styles.cell, { flex: 1, flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'flex-start' }]}>
              <Text style={{ color: 'green', fontWeight: 'bold', fontSize: 13 }}>
                {item.current_new_location || 'empty'}
              </Text>
              <View style={{ marginHorizontal: 50 }}>
                <Text style={{ color: 'green', fontSize: 12, lineHeight: 12 }}>⏶</Text>
                <Text style={{ color: 'red', fontSize: 12, lineHeight: 12 }}>⏷</Text>
              </View>
              <Text style={{ color: 'red', fontWeight: 'bold', fontSize: 13 }}>
                {item.current_old_location || 'empty'}
              </Text>
            </View>           
            
            <View style={styles.cell}>
              <Text>{new Date(item.created_at).toLocaleDateString()}</Text>
            </View>
            {/* <View style={styles.cell}>
              <Text>{item.transferredBy?.name || ''}</Text>
            </View> */}
          </View>
        );
      });
    } else {
      rows.push(
        <View key="no-data" style={[styles.tableRow, { justifyContent: 'center' }]}>
          <Text>No transfer history found for the selected products</Text>
        </View>
      );
    }
    setIsLoading(false);
    return <>{rows}</>;
  };

  // Table component
  const tableElement = useMemo(() => (
    <View style={styles.tableContainer}>
      <View style={styles.tableHeader}>
        <Pressable style={[styles.columnHeader, styles.sortableColumn]} onPress={() => sortData('product.barcode')}>
          <Text>{'Barcode'}</Text>
          {sortColumn === 'product.barcode' && (
            <FontAwesome5 
              name={sortDirection === 'desc' ? "sort-amount-up-alt" : "sort-amount-down-alt"} 
              style={{}} 
            />
          )}
        </Pressable>
        <Pressable style={[styles.columnHeader, styles.sortableColumn]} onPress={() => sortData('product.name')}>
          <Text>{'Product Name'}</Text>
          {sortColumn === 'product.name' && (
            <FontAwesome5 
              name={sortDirection === 'desc' ? "sort-amount-up-alt" : "sort-amount-down-alt"} 
              style={{}} 
            />
          )}
        </Pressable>
        <Pressable style={[styles.columnHeader, styles.sortableColumn, { width: 250 }] } onPress={() => sortData('oldLocation.locations.location')}>
          <Text>{'Home Location'}</Text>
          {sortColumn === 'oldLocation.locations.location' && (
            <FontAwesome5 
              name={sortDirection === 'desc' ? "sort-amount-up-alt" : "sort-amount-down-alt"} 
              style={{}} 
            />
          )}
        </Pressable>
        <Pressable style={[styles.columnHeader, styles.sortableColumn, { width: 250 }] } onPress={() => sortData('newLocation.locations.location')}>
          <Text>{'Current Location'}</Text>
          {sortColumn === 'newLocation.locations.location' && (
            <FontAwesome5 
              name={sortDirection === 'desc' ? "sort-amount-up-alt" : "sort-amount-down-alt"} 
              style={{}} 
            />
          )}
        </Pressable>
        <Pressable style={[styles.columnHeader, styles.sortableColumn]} onPress={() => sortData('transfer_date')}>
          <Text>{'Transfer Date'}</Text>
          {sortColumn === 'transfer_date' && (
            <FontAwesome5 
              name={sortDirection === 'desc' ? "sort-amount-up-alt" : "sort-amount-down-alt"} 
              style={{}} 
            />
          )}
        </Pressable>
        {/* <Pressable style={[styles.columnHeader, styles.sortableColumn]} onPress={() => sortData('transferred_by')}>
          <Text>{'Transferred By'}</Text>
          {sortColumn === 'transferred_by' && (
            <FontAwesome5 
              name={sortDirection === 'desc' ? "sort-amount-up-alt" : "sort-amount-down-alt"} 
              style={{}} 
            />
          )}
        </Pressable> */}
        {/* <Pressable style={[styles.columnHeader, styles.sortableColumn]}>
          <Text>{'Notes'}</Text>
        </Pressable> */}
      </View>
      <View style={{ flex: 1 }}>
        <ScrollView>
          {renderTableData()}
        </ScrollView>
      </View>
    </View>
  ), [filteredAndSortedData, sortColumn, sortDirection]);

  const detailsTable = useMemo(() => {
    if (!selectedBarcode) return null;
  
    return (
      <View style={[styles.tableContainer]}>
        <View style={styles.tableHeader}>
          <Text style={[styles.columnHeader, { flex: 1 }]}>Barcode</Text>
          <Text style={[styles.columnHeader, { flex: 1 }]}>Old Location</Text>
          <Text style={[styles.columnHeader, { flex: 1 }]}>New Location</Text>
          <Text style={[styles.columnHeader, { flex: 1 }]}>Type</Text>
          <Text style={[styles.columnHeader, { flex: 1 }]}>Transfer Date</Text>
        </View>
  
        {isDetailsLoading ? (
          <View style={styles.tableRow}>
            <Text>Loading details...</Text>
          </View>
        ) : barcodeDetails && barcodeDetails.length > 0 ? (
          barcodeDetails.map((row, idx) => (
            <View key={idx} style={styles.tableRow}>
              <View style={[styles.cell, { flex: 1 }]}>
                <Text>{row.barcode}</Text>
              </View>
              <View style={[styles.cell, { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-start' }]}>
                <Text style={{ color: 'red', fontWeight: 'bold' }}>
                  {`${row.oldLocation?.locations?.location || ''} - ${row.oldLocation?.brands?.brand || ''}`} 
                </Text>
              </View>
              <View style={[styles.cell, { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-start' }]}>
                <Text style={{ color: 'green', fontWeight: 'bold' }}>
                  {`${row.newLocation?.locations?.location || ''} - ${row.newLocation?.brands?.brand || ''}`}
                </Text>
              </View> 

              <View style={[styles.cell, { flex: 1 }]}>
                <Text>{row.location_type}</Text>
              </View>

              <View style={[styles.cell, { flex: 1 }]}>
                <Text>{new Date(row.createdAt).toLocaleDateString()}</Text>
              </View>
            </View>
          ))
        ) : (
          <View style={styles.tableRow}>
            <Text>No details found</Text>
          </View>
        )}
  
        <TouchableOpacity
          style={[styles.button, { marginTop: 10, alignSelf: 'flex-end', padding: 5 }]}
          onPress={() => {
            setSelectedBarcode(null);
            setBarcodeDetails(null);
          }}
        >
           <Text style={styles.buttonText}>Close</Text>
        </TouchableOpacity>
      </View>
    );
  }, [selectedBarcode, barcodeDetails, isDetailsLoading]);  

  // If used as a child component, return just the content
  if (isChildComponent) {
    return (
      <View style={[styles.container, containerStyle]}>
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionHeaderText}>Transfer History Log</Text>
      </View>
      {isLoading ? (
        <View style={styles.loadingContainer}>
          <Text>Loading transfer history...</Text>
        </View>
      ) : (
        <ScrollView horizontal={true}>
          {tableElement}
          <View style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
            {selectedBarcode && 
              <View style={{ flex: 1, marginLeft: 20 }}>
                {selectedBarcode && detailsTable}
              </View>
            }
          </View>
        </ScrollView>
      )}
    </View>
    );
  }

  // Otherwise return with full layout (for standalone use)
  return (
    <BasicLayout
      navigation={navigation}
      goBack={() => {
        openInventory(data?.searchOptions ? 'Product Lines' : null);
      }}
      screenName={'Transfer History Log'}
      isLoading={isLoading}
    >
      <ScrollView horizontal={true}>
        <View style={styles.container}>
          {tableElement}
        </View>
      </ScrollView>
    </BasicLayout>
  );
};

const styles = {
  ...productsStyle,
  loadingContainer: {
    padding: 20,
    alignItems: 'center',
  },
  sectionSubHeaderText: {
    fontSize: 14,
    color: '#666',
    marginTop: 5,
  }
};

export default TransferHistoryLog;